import React, { useState, useMemo } from 'react';
import { ArrowLeft, Sparkles, Wrench, ShoppingBag, Pencil, Save, X } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import { buildProductDto, blockOrder, defaultProfileForRole } from 'core/utils/productDto';
import { updateCatalogProductInline } from 'core/api/apiService';
import { ci } from './productDetail/_shared';
import BlockHeader from './productDetail/BlockHeader';
import BlockTechnical from './productDetail/BlockTechnical';
import BlockProcurement from './productDetail/BlockProcurement';
import BlockAvailability from './productDetail/BlockAvailability';
import BlockDocuments from './productDetail/BlockDocuments';
import BlockCompliance from './productDetail/BlockCompliance';

const BLOCKS = {
  technical: BlockTechnical,
  procurement: BlockProcurement,
  availability: BlockAvailability,
  documents: BlockDocuments,
  compliance: BlockCompliance,
};

// Produktdetail-Seite. Rendert ausschließlich das quellen-agnostische DTO.
// Alle Blöcke immer sichtbar; viewProfile steuert nur Reihenfolge/Prominenz.
const ProductDetailView = ({ product, onBack, onAddToCart, viewProfile: viewProfileProp, onProfileChange, user }) => {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const c = ci(isDarkMode);

  const [viewProfileState, setViewProfileState] = useState(viewProfileProp || defaultProfileForRole(user?.role));
  const viewProfile = viewProfileProp || viewProfileState;
  const [quantity, setQuantity] = useState(1);

  // F8: Inline-Edit von Basis-Feldern (gated: admin || can_edit_catalog; SAP read-only)
  const [localProduct, setLocalProduct] = useState(product);
  React.useEffect(() => { setLocalProduct(product); }, [product]);
  const canEdit = user?.role === 'admin' || !!user?.permissions?.can_edit_catalog;
  const editable = canEdit && (localProduct?.source || 'internal') !== 'sap' && localProduct?.id != null;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [draft, setDraft] = useState({ name: '', description: '', price: '' });

  const startEdit = () => {
    setEditError('');
    setDraft({
      name: localProduct.name || '',
      description: localProduct.description || '',
      price: localProduct.price ?? '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!String(draft.name).trim()) { setEditError(t('productDetail.editNameRequired')); return; }
    const priceNum = parseFloat(draft.price);
    if (!Number.isFinite(priceNum) || priceNum < 0) { setEditError(t('productDetail.editPriceInvalid')); return; }
    setSaving(true);
    setEditError('');
    try {
      await updateCatalogProductInline(user, localProduct.shop_id, localProduct.id, {
        name: draft.name.trim(),
        description: draft.description,
        price: priceNum,
      });
      setLocalProduct((p) => ({ ...p, name: draft.name.trim(), description: draft.description, price: priceNum }));
      setEditing(false);
    } catch (e) {
      setEditError(t('productDetail.editError'));
    } finally {
      setSaving(false);
    }
  };

  const dto = useMemo(() => buildProductDto(localProduct, language), [localProduct, language]);

  const setProfile = (vp) => {
    if (onProfileChange) onProfileChange(vp);
    if (!viewProfileProp) setViewProfileState(vp);
  };

  if (!dto) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black' : 'bg-[#F8FAFC]'} flex items-center justify-center`}>
        <p className={c.sub}>{t('productDetail.notFound')}</p>
      </div>
    );
  }

  const TabBtn = ({ value, icon: Icon, label }) => {
    const active = viewProfile === value;
    return (
      <button
        onClick={() => setProfile(value)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
          active
            ? 'bg-brand text-white'
            : isDarkMode ? 'text-white/70 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
        }`}
      >
        <Icon className="w-4 h-4" /> {label}
      </button>
    );
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black' : 'bg-[#F8FAFC]'}`}>
      <div className={`${isDarkMode ? 'bg-gray-900/40 border-b border-white/10 backdrop-blur-xl' : 'bg-white border-b border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={onBack}
            className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800/50 text-white/80 border border-white/10' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
          >
            <ArrowLeft className="w-4 h-4" /> {t('productDetail.back')}
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            {editable && !editing && (
              <button
                onClick={startEdit}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium ${isDarkMode ? 'bg-brand/20 text-blue-200 border border-brand/30 hover:bg-brand/30' : 'bg-brand/10 text-brand border border-brand/20 hover:bg-brand/20'}`}
              >
                <Pencil className="w-4 h-4" /> {t('productDetail.editButton')}
              </button>
            )}
            <div className={`inline-flex items-center gap-1 rounded-xl p-1 ${isDarkMode ? 'bg-gray-800/40 border border-white/10' : 'bg-slate-100'}`}>
              <TabBtn value="technician" icon={Wrench} label={t('productDetail.profileTechnician')} />
              <TabBtn value="buyer" icon={ShoppingBag} label={t('productDetail.profileBuyer')} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {editing && (
          <div className={`${c.card} p-4 space-y-3`}>
            <div className="flex items-center gap-2">
              <Pencil className={`w-4 h-4 ${isDarkMode ? 'text-white/70' : 'text-brand'}`} />
              <span className={`text-sm font-semibold ${c.heading}`}>{t('productDetail.editTitle')}</span>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${c.sub}`}>{t('productDetail.editName')}</label>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                className={`w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:outline-none ${isDarkMode ? 'bg-gray-800/40 border-white/20 text-white focus:ring-blue-400/50' : 'bg-white border-slate-200 text-slate-800 focus:ring-brand/40'}`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${c.sub}`}>{t('productDetail.editDescription')}</label>
              <textarea
                value={draft.description}
                rows={2}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                className={`w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:outline-none ${isDarkMode ? 'bg-gray-800/40 border-white/20 text-white focus:ring-blue-400/50' : 'bg-white border-slate-200 text-slate-800 focus:ring-brand/40'}`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${c.sub}`}>{t('productDetail.editPrice')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={draft.price}
                onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
                className={`w-40 px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:outline-none ${isDarkMode ? 'bg-gray-800/40 border-white/20 text-white focus:ring-blue-400/50' : 'bg-white border-slate-200 text-slate-800 focus:ring-brand/40'}`}
              />
            </div>
            {editError && <p className="text-sm text-red-500">{editError}</p>}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={saveEdit}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white ${saving ? 'bg-brand/50 cursor-not-allowed' : 'bg-brand hover:bg-[#1D4ED8]'}`}
              >
                <Save className="w-4 h-4" /> {saving ? t('productDetail.editSaving') : t('productDetail.editSave')}
              </button>
              <button
                onClick={() => { setEditing(false); setEditError(''); }}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm ${isDarkMode ? 'text-white/70 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <X className="w-4 h-4" /> {t('productDetail.editCancel')}
              </button>
            </div>
          </div>
        )}
        <BlockHeader
          dto={dto}
          t={t}
          isDarkMode={isDarkMode}
          quantity={quantity}
          setQuantity={setQuantity}
          onAddToCart={(qty) => onAddToCart(product, null, qty)}
        />

        {blockOrder(viewProfile).map((key) => {
          const Comp = BLOCKS[key];
          return Comp ? <Comp key={key} dto={dto} t={t} isDarkMode={isDarkMode} /> : null;
        })}

        <div className={`${c.card} p-4 flex items-start gap-3`}>
          <Sparkles className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-white/70' : 'text-brand'}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${c.heading}`}>{t('productDetail.aiAdvisor')}</span>
            </div>
            <p className={`text-sm mt-1 ${c.sub}`}>{t('productDetail.aiAdvisorHint')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailView;
